/* Diagnostics: open any page with ?psdebug=1 (kept for the browsing session) to get a small log at
   the bottom of the screen: which version of this file and of ps-search-slide-motion.css the phone
   is running, every step of opening and closing the search, and how the closing motion actually
   plays, frame by frame. Off by default; changes nothing when off. */
const PS_SEARCH_VERSION = '2026-10-04-h';
const psSearchDebug = (() => {
  const asked = /[?&]psdebug=1/.test(location.search);
  try {
    if (asked) sessionStorage.setItem('psdebug', '1');
    if (/[?&]psdebug=0/.test(location.search)) sessionStorage.removeItem('psdebug');
    return sessionStorage.getItem('psdebug') === '1';
  } catch (_) {
    return asked;
  }
})();
const psLog = (() => {
  if (!psSearchDebug) return () => {};
  const t0 = performance.now();
  let panel = null;
  const lines = [];
  const draw = () => {
    if (!panel) {
      panel = document.createElement('pre');
      panel.style.cssText =
        'position:fixed;left:4px;right:4px;bottom:4px;z-index:2147483647;margin:0;padding:6px 8px;' +
        'max-height:46vh;overflow:hidden;background:rgba(0,0,0,.86);color:#7CFC8A;font:10px/1.35 monospace;' +
        'white-space:pre-wrap;pointer-events:none;border-radius:8px;';
      document.body.appendChild(panel);
    }
    panel.textContent = lines.slice(-26).join('\n');
  };
  window.addEventListener('error', (event) => {
    lines.push(`${Math.round(performance.now() - t0)} ERROR ${event.message} @${(event.filename || '').split('/').pop()}:${event.lineno}`);
    if (document.body) draw();
  });
  return (...parts) => {
    lines.push(`${Math.round(performance.now() - t0)} ${parts.join(' ')}`);
    if (document.body) draw();
  };
})();

(() => {
  const MOBILE = '(max-width: 749px)';

  const bind = () => {
    const modal = document.getElementById('search-modal');
    const dialog = modal?.querySelector('.search-modal__content');

    if (!modal || !dialog || modal.dataset.psSearchMotionBound === 'true') return;
    modal.dataset.psSearchMotionBound = 'true';
    psLog(
      'js', PS_SEARCH_VERSION,
      'css', getComputedStyle(dialog).getPropertyValue('--ps-search-css-version').trim() || '(old)',
      'w', window.innerWidth
    );

    // Keep the search placeholder/text fully clear of the absolutely positioned icon.
    // Native spacing ended before the icon's right edge on mobile, causing the overlap.
    const searchInput = modal.querySelector('.search-input');
    if (searchInput instanceof HTMLInputElement) {
      searchInput.style.setProperty(
        'padding-inline-start',
        'calc(var(--margin-xl) + var(--icon-size-lg) + 12px)',
        'important'
      );
      searchInput.style.setProperty('padding-inline-end', '12px', 'important');
    }

    let sourceTrigger = null;
    let sourceCenterX = null;
    let sourceCenterY = null;
    let sourceWidth = null;
    let openingFrame = 0;
    let resizeFrame = 0;
    /** The icon copy that is travelling right now: { ghost, icon, animation, texts, morph, closing }. */
    let flight = null;

    const px = (value) => `${Math.round(value * 100) / 100}px`;

    const getHeaderParts = (button) => {
      const row =
        button?.closest('.header__row') ||
        document.querySelector('#header-component .header__row--top') ||
        document.querySelector('.header-section .header__row--top');

      const columns = row?.querySelector('.header__columns') || row;
      const paintSource =
        button?.closest('.header-section') ||
        row?.closest('.header-section') ||
        document.querySelector('.header-section') ||
        row;

      return { row, columns, paintSource };
    };

    const syncHeaderMetrics = (button = sourceTrigger) => {
      const { row, columns, paintSource } = getHeaderParts(button);
      if (!row) return;

      const rowRect = row.getBoundingClientRect();
      const spacing = getComputedStyle(columns || row);
      const paint = getComputedStyle(paintSource || row);

      dialog.style.setProperty('--ps-header-top', px(Math.max(0, rowRect.top)));
      dialog.style.setProperty('--ps-header-height', px(rowRect.height));
      dialog.style.setProperty('--ps-header-pad-top', spacing.paddingTop || '0px');
      dialog.style.setProperty('--ps-header-pad-right', spacing.paddingRight || '0px');
      dialog.style.setProperty('--ps-header-pad-bottom', spacing.paddingBottom || '0px');
      dialog.style.setProperty('--ps-header-pad-left', spacing.paddingLeft || '0px');

      dialog.style.setProperty('--ps-header-bg-color', paint.backgroundColor || '#b00813');
      dialog.style.setProperty(
        '--ps-header-bg-image',
        paint.backgroundImage && paint.backgroundImage !== 'none'
          ? paint.backgroundImage
          : 'linear-gradient(105deg, #d10b18 0%, #b00813 55%, #8f060e 100%)'
      );
      dialog.style.setProperty('--ps-header-bg-position', paint.backgroundPosition || '0% 0%');
      dialog.style.setProperty('--ps-header-bg-size', paint.backgroundSize || 'auto');
      dialog.style.setProperty('--ps-header-bg-repeat', paint.backgroundRepeat || 'no-repeat');
    };

    /* Where the motion starts: the search icon of the header button. */
    const measureSource = (button) => {
      sourceTrigger = button;
      const icon = button.querySelector('svg') || button.querySelector('.svg-wrapper') || button;
      const rect = icon.getBoundingClientRect();
      sourceCenterX = rect.left + rect.width / 2;
      sourceCenterY = rect.top + rect.height / 2;
      sourceWidth = rect.width;
    };

    /** The header search button's box and look: the field frame grows out of it and back into it. */
    const sourceBox = () => {
      const button = sourceTrigger?.isConnected ? sourceTrigger : null;
      if (!button) return null;
      const rect = button.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      return { rect, look: lookOf(getComputedStyle(button)) };
    };

    /** @param {CSSStyleDeclaration} style */
    const lookOf = (style) => ({
      borderColor: style.borderTopColor,
      backgroundColor: style.backgroundColor,
      borderRadius: style.borderTopLeftRadius,
      borderWidth: style.borderTopWidth,
    });

    const captureSource = (button) => {
      if (!button || dialog.open || !matchMedia(MOBILE).matches) return;

      measureSource(button);
      syncHeaderMetrics(button);
      dialog.dataset.psSearchMotion = 'preparing';
      psLog('down on search icon x', Math.round(sourceCenterX));
    };

    /* The search icon sits inside the field, and the field opens by stretching from zero width
       while it fades in. The icon was squeezed and faded along with it: it disappeared when the
       strip came up and returned as a thin sliver stretching back into shape. A copy of the icon,
       placed on the strip outside the field, now makes the trip from the header icon to the icon's
       place in the field at its real size, with the field's own duration and curve. The real icon
       stays hidden until the copy has landed exactly on top of it. The field stretches as before. */
    const endFlight = () => {
      if (!flight) return;
      const { ghost, icon, animation, texts, morph } = flight;
      flight = null;
      animation.cancel();
      texts.forEach(({ animation: textAnimation }) => textAnimation.cancel());
      ghost.remove();
      icon.style.removeProperty('visibility');
      if (morph) {
        morph.animation.cancel();
        morph.element.remove();
      }
      delete dialog.dataset.psSearchMorph;
    };

    /* The frame of the field grows out of the header search button's square and shrinks back into
       it: one box that goes from the button's place, size, corners and colours to the field's and
       back. The square never disappears or jumps; when the search has closed, the real button is
       exactly where the box stopped. While it moves, the field's own frame (CSS ::before) is hidden;
       the box sits under the text and the icon. */
    const morphFrame = (fromBox, toBox, timing) => {
      const strip = modal.querySelector('.predictive-search-form__header');
      if (!strip) return null;

      const element = document.createElement('div');
      element.setAttribute('aria-hidden', 'true');
      element.style.cssText =
        `position:fixed;z-index:0;margin:0;padding:0;box-sizing:border-box;pointer-events:none;` +
        `border-style:solid;border-width:${fromBox.look.borderWidth || '1px'};will-change:left,top,width,height;`;

      const frame = (box) => ({
        left: px(box.rect.left),
        top: px(box.rect.top),
        width: px(box.rect.width),
        height: px(box.rect.height),
        borderColor: box.look.borderColor,
        backgroundColor: box.look.backgroundColor,
        borderRadius: box.look.borderRadius,
      });

      strip.appendChild(element);
      const animation = animateSafely(element, [frame(fromBox), frame(toBox)], timing);
      dialog.dataset.psSearchMorph = '';
      return { element, animation };
    };

    /** The field frame's box and look, in the final layout. */
    const fieldBox = () => {
      const field = modal.querySelector('.predictive-search-form__header-inner');
      if (!field) return null;
      const rect = field.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      return { rect, look: lookOf(getComputedStyle(field, '::before')) };
    };

    /* A CSS time in milliseconds. Shopify serves the stylesheet minified, which rewrites 280ms as
       .28s: read as a plain number that was 0.28 ms, so every scripted motion jumped straight to
       its end on the live site (seen with ?psdebug=1: "ms 0.28"). */
    const toMs = (value, fallback) => {
      const text = String(value || '').trim();
      const number = parseFloat(text);
      if (!Number.isFinite(number)) return fallback;
      if (/ms$/i.test(text)) return number;
      if (/s$/i.test(text)) return number * 1000;
      return number;
    };

    const motionTiming = () => {
      const motion = getComputedStyle(dialog);
      return {
        duration: toMs(motion.getPropertyValue('--ps-motion-duration'), 280) || 280,
        easing: motion.getPropertyValue('--ps-motion-ease').trim() || 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'both',
      };
    };

    /** @param {Element} target @param {Keyframe[]} keyframes @param {object} timing */
    const animateSafely = (target, keyframes, timing) => {
      try {
        return target.animate(keyframes, timing);
      } catch (_) {
        return target.animate(keyframes, { ...timing, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
      }
    };

    /* The field text is hidden behind the travelling icon: it is uncovered right behind the icon
       as the icon passes (opening) and covered again as the icon goes back (closing). The edge of
       the uncovered part is the trailing side of the icon, on the same duration and curve, so the
       two move as one. When the icon only moves a little (header icon and field icon on the same
       side), the edge follows the frame instead, from the header icon across the field. */
    const revealPlan = (destRect) => {
      const field = modal.querySelector('.predictive-search-form__header-inner');
      const fieldRect = field?.getBoundingClientRect();
      if (!fieldRect || sourceCenterX === null) return null;

      const half = destRect.width / 2;
      const destCenter = destRect.left + half;
      const fromRight = sourceCenterX >= window.innerWidth / 2;
      const behind = fromRight ? half : -half;

      if (Math.abs(sourceCenterX - destCenter) >= 40) {
        return { fromRight, hidden: sourceCenterX + behind, shown: destCenter + behind };
      }
      return { fromRight, hidden: sourceCenterX, shown: fromRight ? fieldRect.left : fieldRect.right };
    };

    const textTargets = () =>
      [modal.querySelector('.search-input'), modal.querySelector('.predictive-search__reset-button')].filter(
        (element) => element instanceof HTMLElement && element.getClientRects().length > 0
      );

    /** The clip that shows a text element only on the far side of `edge` (a screen x). */
    const clipAt = (element, plan, edge) => {
      const box = element.getBoundingClientRect();
      return plan.fromRight
        ? `inset(0px 0px 0px ${px(edge - box.left)})`
        : `inset(0px ${px(box.right - edge)} 0px 0px)`;
    };

    /** A copy of the icon glyph, placed exactly over it on the search strip (outside the field). */
    const makeGhost = (glyph, rect) => {
      const ghost = glyph.cloneNode(true);
      ghost.setAttribute('aria-hidden', 'true');
      ghost.style.cssText =
        `position:fixed;left:${px(rect.left)};top:${px(rect.top)};width:${px(rect.width)};height:${px(rect.height)};` +
        `margin:0;z-index:2;pointer-events:none;color:${getComputedStyle(glyph).color};will-change:transform;`;

      /* The copy is drawn exactly like the original, whatever CSS reaches the original through
         its parents (the copy has other parents). */
      const shapes = glyph.querySelectorAll('*');
      ghost.querySelectorAll('*').forEach((copy, index) => {
        const original = shapes[index];
        if (!original || !(copy instanceof SVGElement)) return;
        const drawn = getComputedStyle(original);
        copy.style.fill = drawn.fill;
        copy.style.stroke = drawn.stroke;
        copy.style.strokeWidth = drawn.strokeWidth;
      });
      return ghost;
    };

    /** Where the header icon is, relative to the field icon, and its size relative to it. */
    const headerOffset = (rect) => {
      const x = sourceCenterX === null ? 0 : sourceCenterX - (rect.left + rect.width / 2);
      let y = sourceCenterY === null ? 0 : sourceCenterY - (rect.top + rect.height / 2);
      if (Math.abs(y) > 40) y = 0;
      /* The header icon and the field icon can differ in size by a pixel or two. */
      const scale = sourceWidth ? Math.min(1.5, Math.max(0.67, sourceWidth / rect.width)) : 1;
      return { x, y, scale };
    };

    /* Opening: the icon travels from the header icon to its place in the field, the text comes
       out from behind it. */
    const startFlight = (icon) => {
      endFlight();

      const strip = modal.querySelector('.predictive-search-form__header');
      const glyph = icon.querySelector('svg');
      if (!strip || !glyph) return psLog('open: no strip/glyph');

      /* Measured in the final layout (state 'preparing'): this is where the icon ends up. */
      const rect = glyph.getBoundingClientRect();
      if (!rect.width || !rect.height) return psLog('open: icon has no size');

      const timing = motionTiming();
      const start = headerOffset(rect);
      const ghost = makeGhost(glyph, rect);

      strip.appendChild(ghost);
      const animation = animateSafely(
        ghost,
        [
          { transform: `translate(${px(start.x)}, ${px(start.y)}) scale(${start.scale})` },
          { transform: 'translate(0px, 0px) scale(1)' },
        ],
        timing
      );
      icon.style.setProperty('visibility', 'hidden');

      const plan = revealPlan(rect);
      const texts = plan
        ? textTargets().map((element) => ({
            element,
            animation: animateSafely(
              element,
              [{ clipPath: clipAt(element, plan, plan.hidden) }, { clipPath: clipAt(element, plan, plan.shown) }],
              timing
            ),
          }))
        : [];

      const from = sourceBox();
      const to = fieldBox();
      const morph = from && to ? morphFrame(from, to, timing) : null;
      psLog('open: icon', Math.round(start.x), 'text', texts.length, 'box', morph ? 'yes' : `no(${from ? '' : 'button '}${to ? '' : 'field'})`, 'ms', timing.duration);

      flight = { ghost, icon, animation, texts, morph, closing: false };
      animation.finished.then(
        () => {
          if (flight?.animation === animation) endFlight();
        },
        () => {}
      );
    };

    /* Closing: the same motion backwards. The icon travels back to the header icon, the text goes
       back behind it, the frame shrinks into the header icon (CSS). Starts from wherever things
       are, also in the middle of an opening. The copy stays on the header icon until the search
       has closed; then the real header icon is in exactly the same place. */
    const startReverse = () => {
      const icon = modal.querySelector('.predictive-search__icon');
      const glyph = icon?.querySelector('svg');
      const strip = modal.querySelector('.predictive-search-form__header');

      let currentRect = null;
      let currentFrame = null;
      const currentClips = new Map();
      if (flight && !flight.closing) {
        currentRect = flight.ghost.getBoundingClientRect();
        flight.texts.forEach(({ element }) => currentClips.set(element, getComputedStyle(element).clipPath));
        if (flight.morph) {
          const box = flight.morph.element;
          currentFrame = { rect: box.getBoundingClientRect(), look: lookOf(getComputedStyle(box)) };
        }
      }
      endFlight();

      if (!icon || !glyph || !strip) return psLog('close: no icon/strip');

      if (sourceCenterX === null) {
        const onScreen = [...document.querySelectorAll('.search-modal__button')].find(
          (button) => button.getClientRects().length > 0
        );
        if (onScreen) measureSource(onScreen);
      }
      if (sourceCenterX === null) return psLog('close: no header icon');

      const rect = glyph.getBoundingClientRect();
      if (!rect.width || !rect.height) return psLog('close: icon has no size');

      const timing = motionTiming();
      const to = headerOffset(rect);
      const startX = currentRect ? currentRect.left + currentRect.width / 2 - (rect.left + rect.width / 2) : 0;
      const startY = currentRect ? currentRect.top + currentRect.height / 2 - (rect.top + rect.height / 2) : 0;
      const startScale = currentRect ? currentRect.width / rect.width : 1;

      const ghost = makeGhost(glyph, rect);
      strip.appendChild(ghost);
      const animation = animateSafely(
        ghost,
        [
          { transform: `translate(${px(startX)}, ${px(startY)}) scale(${startScale})` },
          { transform: `translate(${px(to.x)}, ${px(to.y)}) scale(${to.scale})` },
        ],
        timing
      );
      icon.style.setProperty('visibility', 'hidden');

      const plan = revealPlan(rect);
      const texts = plan
        ? textTargets().map((element) => {
            const current = currentClips.get(element);
            const start = current && current !== 'none' ? current : clipAt(element, plan, plan.shown);
            return {
              element,
              animation: animateSafely(element, [{ clipPath: start }, { clipPath: clipAt(element, plan, plan.hidden) }], timing),
            };
          })
        : [];

      const target = sourceBox();
      const startFrame = currentFrame || fieldBox();
      const morph = target && startFrame ? morphFrame(startFrame, target, timing) : null;

      flight = { ghost, icon, animation, texts, morph, closing: true };
      psLog('close: icon', Math.round(to.x), 'text', texts.length, 'box', morph ? 'yes' : 'no', 'ms', timing.duration);

      if (psSearchDebug) {
        /* How the closing really plays: every ~40ms, the icon's progress and the box's left/width. */
        const began = performance.now();
        const samples = [];
        const sample = () => {
          const elapsed = Math.round(performance.now() - began);
          const box = morph?.element.getBoundingClientRect();
          samples.push(`${elapsed}:${Math.round(animation.currentTime ?? -1)}/${animation.playState[0]}${box ? `@${Math.round(box.left)}+${Math.round(box.width)}` : ''}`);
          if (elapsed < 420 && dialog.open) {
            setTimeout(() => requestAnimationFrame(sample), 30);
          } else {
            psLog('close frames', samples.join(' '));
          }
        };
        requestAnimationFrame(sample);
      }
    };

    /* The field is the header search button, grown: the same height, and its edge on the button's
       side lies exactly on the button's outer edge. So the frame only grows away from the button
       (top, bottom and that edge never move) and shrinks back into the very same square. The
       field used to be 4px taller than the button and to end 8px short of its right edge, which
       made the square jump when the motion started and again when it ended. */
    const alignFieldToButton = (field) => {
      dialog.style.removeProperty('--ps-field-height');
      dialog.style.setProperty('--ps-field-shift-left', '0px');
      dialog.style.setProperty('--ps-field-shift-right', '0px');

      const box = sourceBox();
      if (!box) return;

      dialog.style.setProperty('--ps-field-height', px(box.rect.height));
      const fieldRect = field.getBoundingClientRect();
      if (!fieldRect.width) return;

      const fromRight = box.rect.left + box.rect.width / 2 >= window.innerWidth / 2;
      if (fromRight) {
        dialog.style.setProperty('--ps-field-shift-right', px(fieldRect.right - box.rect.right));
      } else {
        dialog.style.setProperty('--ps-field-shift-left', px(box.rect.left - fieldRect.left));
      }
    };

    const prepareOpen = () => {
      cancelAnimationFrame(openingFrame);

      openingFrame = requestAnimationFrame(() => {
        if (!dialog.open) return;

        const destinationIcon = modal.querySelector('.predictive-search__icon');
        const field = modal.querySelector('.predictive-search-form__header-inner');
        if (!destinationIcon || !field) return;

        alignFieldToButton(field);

        const iconRect = destinationIcon.getBoundingClientRect();
        const fieldRect = field.getBoundingClientRect();
        const destinationIconCenter = iconRect.left + iconRect.width / 2;
        const sourceX = sourceCenterX ?? destinationIconCenter;
        const sourceIsRight = sourceX >= window.innerWidth / 2;
        const fieldAnchor = sourceIsRight ? fieldRect.right : fieldRect.left;

        dialog.style.setProperty('--ps-icon-from-x', px(sourceX - destinationIconCenter));
        dialog.style.setProperty('--ps-bar-from-x', px(sourceX - fieldAnchor));
        dialog.style.setProperty('--ps-bar-origin', sourceIsRight ? '100% 50%' : '0% 50%');

        startFlight(destinationIcon);

        dialog.dataset.psSearchMotion = 'ready';
        void dialog.offsetWidth;
        dialog.dataset.psSearchMotion = 'open';
      });
    };

    const beginClose = (why = '') => {
      if (!dialog.open || !matchMedia(MOBILE).matches) return;
      /* Called by the X tap and again when the dialog starts closing: run the motion once. */
      if (dialog.dataset.psSearchMotion === 'closing') return;
      psLog('close starts', why, 'state', dialog.dataset.psSearchMotion || '-', dialog.classList.contains('dialog-closing') ? 'dialog-closing' : '');
      try {
        startReverse();
      } catch (error) {
        psLog('close ERROR', error && error.message);
        throw error;
      }
      dialog.dataset.psSearchMotion = 'closing';
    };

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const opener = event.target.closest('.search-modal__button');
        if (opener) captureSource(opener);

        if (event.target.closest('.predictive-search__close-modal-button')) {
          modal.querySelector('.search-input')?.blur();
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const opener = event.target.closest('.search-modal__button');
        if (opener && sourceCenterX === null) captureSource(opener);

        if (event.target.closest('.predictive-search__close-modal-button')) {
          beginClose('x-click');
        }
      },
      true
    );

    new MutationObserver(() => {
      if (dialog.classList.contains('dialog-closing')) beginClose('dialog-closing');
    }).observe(dialog, { attributes: true, attributeFilter: ['class'] });

    if (psSearchDebug) {
      /* Taps on the X: does each one become a click, and is anything cancelling it? Logged at the
         very end of each event (bubble phase on window), so every other script has run by then. */
      const name = (node) =>
        node instanceof Element
          ? `${node.tagName.toLowerCase()}${node.classList.length ? '.' + [...node.classList].slice(0, 2).join('.') : ''}`
          : String(node);
      const onX = (event) => event.target instanceof Element && event.target.closest('.predictive-search__close-modal-button');
      ['pointerdown', 'pointerup', 'pointercancel', 'touchend', 'touchcancel', 'click'].forEach((type) => {
        window.addEventListener(type, (event) => {
          if (!dialog.open) return;
          if (!onX(event) && type !== 'click' && type !== 'pointercancel' && type !== 'touchcancel') return;
          const point = event.clientX !== undefined ? document.elementFromPoint(event.clientX, event.clientY) : null;
          psLog(
            'X', type, name(event.target),
            point && point !== event.target ? 'under:' + name(point) : '',
            event.defaultPrevented ? 'PREVENTED' : '',
            dialog.classList.contains('dialog-closing') ? 'closing' : '',
            flight ? (flight.closing ? 'flight:close' : 'flight:open') : ''
          );
        });
      });
    }

    modal.addEventListener('dialog:open', () => {
      if (!matchMedia(MOBILE).matches) return;
      psLog('dialog open');

      /* The search can open without a finger-down on the button having been seen first, e.g. a
         tap that began while the previous search was still closing (the page takes no touches
         until it has closed) and ended on the search button. The click handler above comes too
         late for that case: the theme opens the dialog before it runs. Without a starting point
         the motion used to begin at the icon's place inside the field, on the other side of the
         screen. The header has two search buttons (one per screen size); use the one on screen. */
      if (sourceCenterX === null) {
        const onScreen = [...document.querySelectorAll('.search-modal__button')].find(
          (button) => button.getClientRects().length > 0
        );
        if (onScreen) measureSource(onScreen);
      }

      syncHeaderMetrics();
      prepareOpen();
    });

    modal.addEventListener('dialog:close', () => {
      psLog('dialog closed');
      cancelAnimationFrame(openingFrame);
      endFlight();
      delete dialog.dataset.psSearchMotion;
      sourceTrigger = null;
      sourceCenterX = null;
      sourceCenterY = null;
      sourceWidth = null;

      [
        '--ps-icon-from-x',
        '--ps-bar-from-x',
        '--ps-bar-origin',
        '--ps-field-height',
        '--ps-field-shift-left',
        '--ps-field-shift-right',
        '--ps-header-top',
        '--ps-header-height',
        '--ps-header-pad-top',
        '--ps-header-pad-right',
        '--ps-header-pad-bottom',
        '--ps-header-pad-left',
        '--ps-header-bg-color',
        '--ps-header-bg-image',
        '--ps-header-bg-position',
        '--ps-header-bg-size',
        '--ps-header-bg-repeat',
      ].forEach((property) => dialog.style.removeProperty(property));
    });

    window.addEventListener(
      'resize',
      () => {
        if (!dialog.open || !matchMedia(MOBILE).matches) return;
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => syncHeaderMetrics());
      },
      { passive: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();

// Page underlay for mobile search: a fixed layer that dims the page behind the search.
// Performance: it no longer uses backdrop-filter. The old 1px blur was barely visible, but a
// full-screen backdrop blur has to be recomputed on every frame while the search slides in and
// out, which is what made opening/closing search stutter on phones.
(() => {
  const MOBILE = '(max-width: 749px)';
  const STYLE_ID = 'ps-search-real-underlay-style';
  const LAYER_ID = 'ps-search-page-underlay';
  const DURATION = 240;

  const installStyle = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      @media (max-width: 749px) {
        #search-modal .search-modal__content::backdrop {
          background: transparent !important;
          -webkit-backdrop-filter: none !important;
          backdrop-filter: none !important;
        }

        #${LAYER_ID} {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          pointer-events: none;
          background: rgb(0 0 0 / 45%);
          opacity: 0;
          visibility: hidden;
          transition: opacity ${DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${DURATION}ms;
        }

        #${LAYER_ID}.is-visible {
          opacity: 1;
          visibility: visible;
          transition: opacity ${DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 0s;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const ensureLayer = () => {
    let layer = document.getElementById(LAYER_ID);
    if (!layer) {
      layer = document.createElement('div');
      layer.id = LAYER_ID;
      layer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(layer);
    }
    return layer;
  };

  /* The dim layer starts where the search strip ends, i.e. below the header. Laid over the header
     as well, it darkened the red bar from the moment the finger touched the search icon, and the
     bar then jumped back to full brightness when the search strip appeared on top of it (on
     closing, the strip faded away over a header that was still darkened). The strip covers the
     header exactly, so nothing else changes: the page below dims as before. */
  const stripBottom = () => {
    const dialog = document.querySelector('#search-modal .search-modal__content');
    const top = parseFloat(dialog?.style.getPropertyValue('--ps-header-top') ?? '');
    const height = parseFloat(dialog?.style.getPropertyValue('--ps-header-height') ?? '');
    if (Number.isFinite(top) && Number.isFinite(height)) return top + height;

    const row =
      document.querySelector('#header-component .header__row--top') ||
      document.querySelector('.header-section .header__row--top');
    return row ? Math.max(0, row.getBoundingClientRect().bottom) : 0;
  };

  const show = () => {
    if (!matchMedia(MOBILE).matches) return;
    installStyle();
    const layer = ensureLayer();
    layer.style.top = `${stripBottom()}px`;
    requestAnimationFrame(() => layer.classList.add('is-visible'));
  };

  const hide = () => {
    document.getElementById(LAYER_ID)?.classList.remove('is-visible');
  };

  const bind = () => {
    const modal = document.getElementById('search-modal');
    const dialog = modal?.querySelector('.search-modal__content');
    if (!modal || !dialog || modal.dataset.psRealUnderlayBound === 'true') return;
    modal.dataset.psRealUnderlayBound = 'true';

    installStyle();

    document.addEventListener('pointerdown', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('.search-modal__button')) show();
      if (event.target.closest('.predictive-search__close-modal-button')) hide();
    }, true);

    modal.addEventListener('dialog:open', show);
    modal.addEventListener('dialog:close', hide);

    new MutationObserver(() => {
      if (dialog.classList.contains('dialog-closing')) hide();
      else if (dialog.open) show();
    }).observe(dialog, { attributes: true, attributeFilter: ['class', 'open'] });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
